import {
    forwardRef,
    useImperativeHandle, useMemo, useState
} from 'react';
import { Form, FormFeedback, FormGroup, Label } from 'reactstrap';
import { twMerge } from 'tailwind-merge';
import Select, { SingleValue } from 'react-select';

import { EmployeeConfigMaster } from "../../../models/config/EmployeeConfigMaster";
import { useCompanyMaster, useEmployees } from "../../../queries";
import { CompanyMaster } from 'models/companymaster/CompanyMaster';
import { EmployeeMain } from 'models/config/Employee';
import { useRoleMaster } from 'presentation/LegacyRoleMasterConfig/queries';
import { RoleMaster } from 'models/config/RoleMaster';
import { useUserInformation } from 'hooks';

export type EmployeeConfigMasterFormRefHandle = {
    submit: () => EmployeeConfigMaster | undefined;
};

export type EmployeeConfigMasterFormProps = {
    disabledKeyField?: boolean;
    defaultValues?: EmployeeConfigMaster;
    className?: string;
};

export const EmployeeConfigMasterForm = forwardRef<
    EmployeeConfigMasterFormRefHandle,
    EmployeeConfigMasterFormProps
>((props, ref) => {
    const { disabledKeyField, defaultValues, className } = props;

    const [formValues, setFormValues] = useState<
        Partial<EmployeeConfigMaster>
    >({ ...defaultValues });
    const [errors, setErrors] = useState<Record<keyof EmployeeConfigMaster, string>>(
        {} as EmployeeConfigMaster
    );

    const { data: companyMaster } = useCompanyMaster();
    const { data: employees } = useEmployees();
    const { data: roleMaster } = useRoleMaster();

	const uniqueRoleMaster = useMemo(() => {
		if (!roleMaster) return [];

		const map = new Map<string, RoleMaster>();

		roleMaster.forEach((role) => {
			if (!map.has(role.id)) {
				map.set(role.id, role);
			}
		});

		return Array.from(map.values());
	}, [roleMaster]);

	const { userCompanySids, companyOptions } = useUserInformation()
	// const companyOptions = useMemo(() => {
	// 	if (!companyMaster || companyMaster.length === 0) return [];

	// 	const allowedCompanies = companyMaster.filter((c) =>
	// 		userCompanies.includes(c.sid)
	// 	);

	// 	// console.log("companyMaster:", companyMaster);
	// 	// console.log("userCompanies:", userCompanies);
	// 	// console.log("allowedCompanies:", allowedCompanies);

	// 	const baseOptions: CompanyMaster[] = [
	// 		// { sid: "all", company_name: "เลือกทั้งหมด" } as CompanyMaster,
	// 	];

	// 	if (userCompanies.includes("*")) {
	// 		baseOptions.push({
	// 			sid: "*",
	// 			company_name: "*",
	// 		} as CompanyMaster);
	// 	}

	// 	const options = baseOptions.concat(allowedCompanies);

	// 	// console.log("allowedCompanies:", allowedCompanies);
	// 	// console.log("options:", options);
	// 	// console.log('roleMaster', roleMaster)

	// 	return options;
	// }, [companyMaster, userCompanies]);

	const employeeWithSameRole = useMemo(() => {
		if (!employees || !formValues.role_access) return [];

		const { sid: selectedCompanySid, role_access } = formValues;
		if (!selectedCompanySid) return [];

		// Filter employees who belong to the selected company AND have the same role_access
		const filtered = employees.filter((employee) =>
			employee.list_company.some(
				(company) =>
					company.sid === selectedCompanySid &&
					company.role_access === role_access
			)
		);

		// Remove duplicates by employee id
		const uniqueEmployeeMap = new Map<string, typeof filtered[0]>();
		filtered.forEach((emp) => {
			if (!uniqueEmployeeMap.has(emp.id)) {
				uniqueEmployeeMap.set(emp.id, emp);
			}
		});

		return Array.from(uniqueEmployeeMap.values());
	}, [employees, formValues.role_access, formValues.sid]);

	const selectedCompanySid = formValues.sid;
	const employeeOptions = useMemo(() => {
		if (!employees?.length) {
			return [];
		}

		// Track employee who already have this role
		const employeeWithSameRoleIds = new Set(
			employeeWithSameRole.map((emp) => emp.id)
		);

		const isSuperAdmin = userCompanySids.includes("*");

		let availableEmployee: EmployeeMain[] = [];
		if (isSuperAdmin) {
			// SUPER ADMIN: Can see all or specific company employee
			const isAllCompanySelected =
				!selectedCompanySid || selectedCompanySid === "*";

			availableEmployee = isAllCompanySelected
				? employees
				: employees.filter((emp) =>
						emp.list_company.some(
							(company) => company.sid === selectedCompanySid
						)
				  );
		} else {
			// NORMAL USER: Can only see employee from their company
			const isCompanySelected = Boolean(selectedCompanySid);

			const employeeInUserCompanySids = employees.filter((emp) =>
				emp.list_company.some((company) => userCompanySids.includes(company.sid))
			);

			availableEmployee = isCompanySelected
				? employeeInUserCompanySids.filter((emp) =>
						emp.list_company.some(
							(company) => company.sid === selectedCompanySid
						)
				  )
				: employeeInUserCompanySids;
		}

		// Remove employee who already have the same role
		const filteredEmployee = availableEmployee.filter(
			(emp) => !employeeWithSameRoleIds.has(emp.id)
		);

		return filteredEmployee;
	}, [employees, userCompanySids, selectedCompanySid, employeeWithSameRole]);

    const handleSelectCompany = (
        newSelectedCompany: SingleValue<CompanyMaster>
    ) => {
        if (!newSelectedCompany) {
            return;
        }

		// console.log('newSelectedCompany', newSelectedCompany)

        setFormValues({
            ...formValues,
            sid: newSelectedCompany.sid,
			id: "", // Reset selected employee
        });
    };

    const handleSelectEmployee = (
        newSelectedEmployee: SingleValue<EmployeeMain>
    ) => {
        if (!newSelectedEmployee) {
            return;
        }

        setFormValues({
            ...formValues,
            id: newSelectedEmployee.id
        });
    };

    const handleSelectRoleAccess = (
        newSelectedRoleAccess: SingleValue<RoleMaster>
    ) => {
        if (!newSelectedRoleAccess) {
            return;
        }

        setFormValues({
            ...formValues,
            role_access: newSelectedRoleAccess.id
        });
    };

    const submitForm = () => {
        const errors: [keyof EmployeeConfigMaster, string][] = [];
        if (!formValues.sid) {
            errors.push(['sid', 'กรุณาเลือก Company']);
        }
        if (!formValues.id) {
            errors.push(['id', 'กรุณากรอก Menu ID']);
        }
        if (!formValues.role_access) {
            errors.push(['role_access', 'กรุณาเลือก Role']);
        }

        if (errors.length > 0) {
            setErrors(prevState => ({
                ...prevState,
                // @ts-ignore
                ...Object.fromEntries(errors)
            }));

            return undefined;
        }

        return { ...formValues } as EmployeeConfigMaster;
    };

    useImperativeHandle(
        ref,
        () => ({
            submit: submitForm
        }),
        [submitForm]
    );

    return (
        <Form className={twMerge('tw-flex tw-flex-col', className)}>
            <FormGroup>
                <Label for="sid">Company</Label>
                <Select
                    isDisabled={disabledKeyField}
                    options={companyOptions}
                    value={companyOptions?.find(
                        company => company.sid === formValues.sid
                    )}
                    getOptionLabel={(company: CompanyMaster) =>
                        company.company_name
                    }
                    getOptionValue={(company: CompanyMaster) => company.sid}
                    onChange={handleSelectCompany}
                />
                <FormFeedback>{errors.sid}</FormFeedback>
            </FormGroup>
            <FormGroup>
                <Label for="sid">Role Access</Label>
                {/* <Select
                    isDisabled={disabledKeyField || !formValues.sid}
                    options={roleMaster}
                    value={roleMaster?.find(
                        role => role.id === formValues.role_access
                    )}
                    getOptionLabel={(role: RoleMaster) => role.id}
                    getOptionValue={(role: RoleMaster) => role.id}
                    onChange={handleSelectRoleAccess}
                /> */}
				<Select
					isDisabled={disabledKeyField || !formValues.sid}
					options={uniqueRoleMaster}
					value={uniqueRoleMaster.find(
						(role) => role.id === formValues.role_access
					)}
					getOptionLabel={(role: RoleMaster) => role.id}
					getOptionValue={(role: RoleMaster) => role.id}
					onChange={handleSelectRoleAccess}
				/>

                <FormFeedback>{errors.id}</FormFeedback>
            </FormGroup>
			<FormGroup>
                <Label for="sid">Current employee with the same role</Label>
				<Select
					isMulti
					isDisabled={true}
					options={employeeWithSameRole}
					value={employeeWithSameRole.sort((a, b) => a.id.localeCompare(b.id))}
					getOptionLabel={(emp) => emp.id}
					getOptionValue={(emp) => emp.id}
				/>
                <FormFeedback>{errors.id}</FormFeedback>
            </FormGroup>
            <FormGroup>
                <Label for="sid">Employee</Label>
                <Select
                    isDisabled={disabledKeyField || !formValues.sid}
                    // options={employees}
                    options={employeeOptions}
                    value={employees?.find(
                        employee => employee.id === formValues.id
                    )}
                    getOptionLabel={(employee: EmployeeMain) => employee.id}
                    getOptionValue={(employee: EmployeeMain) => employee.id}
                    onChange={handleSelectEmployee}
                />
                <FormFeedback>{errors.id}</FormFeedback>
            </FormGroup>


        </Form>
    );
});

EmployeeConfigMasterForm.displayName = 'EmployeeConfigMasterForm';
